import React from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import htmlReactParser, { attributesToProps } from 'html-react-parser';
import PropTypes from 'prop-types';
import styles from '@utils/styles';
import breakpoints from '@rsc/ui-components/breakpoints';
import PageWrapper from '@rsc/ui-components/page-wrapper/PageWrapper';

const Heading = styled.h1`
  color: ${styles.colors.gray[700]};
  font-weight: ${styles.fontWeight.medium};
  font-size: ${styles.fontSize['3xl']};
  margin: 0 0 10px 0;
  line-height: 40px;
`;

const BlogImageContainer = styled.div`
  text-align: center;
  margin-bottom: 1rem;

  img {
    max-width: 100%;
    height: auto;
  }
`;

const BlogPost = styled.div`
  padding: 0;
  margin: 0 auto;

  h2 {
    font-size: ${styles.fontSize['2xl']};
  }

  @media (min-width: ${breakpoints.md}) {
    padding: 0;
    max-width: 75ch;
  }

  img {
    max-width: 100%;
  }

  p {
    color: ${styles.colors.gray[700]};
    margin-bottom: 25px;
  }

  iframe {
    width: 100%;
    aspect-ratio: 16 / 9;
    border: medium none;
  }
`;

const BlogPostBottom = styled.div`
  display: flex;
  margin-top: 1rem;
  flex-direction: column;

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`;

const TagsContainer = styled.div`
  a {
    color: ${styles.colors.gray[400]};
  }

  @media (min-width: ${breakpoints.md}) {
    width: 50%;
    text-align: left;
  }
`;

const BlogTagContainer = styled.div`
  font-size: ${styles.fontSize.sm};
  font-weight: ${styles.fontWeight.regular};
  color: ${styles.colors.gray[100]};
  margin-bottom: 15px;
  display: inline-block;
  padding: 0;
  margin-right: 0.625rem;
  font-size: ${styles.fontSize.base};

  a {
    padding: 0.625rem 1rem;
    font-weight: ${styles.fontWeight.medium};
    color: ${styles.colors.blue[500]};
    background: ${styles.colors.gray[100]};
    display: inline-block;
  }
`;

const GetNth = dayOfMonth => {
  if (dayOfMonth > 3 && dayOfMonth < 21) return 'th';
  switch (dayOfMonth % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const GetFormattedPostDate = (author, publishedAt) => {
  const dateObject = new Date(publishedAt);
  const month = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ][dateObject.getMonth()];

  return (
    <p>
      {author !== '' && author !== 'DM Devs' && `Posted by ${author} on `}
      {dateObject.getDate()}
      <sup>{GetNth(dateObject.getDate())}</sup> {month}{' '}
      {dateObject.getFullYear()}
    </p>
  );
};

const CreateAriaLabel = tag => `articles tagged ${tag}`;
const FormatTagHandle = tag => `/blog/tag/${tag.replace(/ /g, '+')}`;

const articleFormattingOptions = {
  replace: domNode => {
    // add lazy loading to all article images and replace with picture
    if (domNode.name === 'img') {
      const props = attributesToProps(domNode.attribs);
      // TODO: Get bet breakpoints for images
      return (
        <picture>
          <source
            srcSet={`${props.src}?width=320`}
            media="(max-width: 320px)"
          />
          <source
            srcSet={`${props.src}?width=400`}
            media="(max-width: 600px)"
          />
          <source
            srcSet={`${props.src}?width=1200`}
            media="(max-width: 1200px)"
          />
          <source
            srcSet={`${props.src}?width=1920`}
            media="(max-width: 1920px)"
          />
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <img alt="" {...props} loading="lazy" />
        </picture>
      );
    }
    return domNode;
  },
};

const BlogArticle = ({ article }) => (
  <PageWrapper>
    {article?.image?.url && (
      <BlogImageContainer>
        <picture>
          <source
            srcSet={`${article.image.url}&width=320`}
            media="(max-width: 320px)"
          />
          <source
            srcSet={`${article.image.url}&width=400`}
            media="(max-width: 600px)"
          />
          <source
            srcSet={`${article.image.url}&width=1200`}
            media="(max-width: 1200px)"
          />
          <source
            srcSet={`${article.image.url}&width=1920`}
            media="(max-width: 1920px)"
          />
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img
            src={article.image.url}
            alt={article.image?.altText}
            loading="eager"
          />
        </picture>
      </BlogImageContainer>
    )}
    <BlogPost>
      <div>
        <Heading>{article.title}</Heading>
        {GetFormattedPostDate(article.authorV2.name, article.publishedAt)}
      </div>
      {htmlReactParser(article.contentHtml, articleFormattingOptions)}
      <BlogPostBottom>
        <TagsContainer>
          {article.tags.map(tag => (
            <BlogTagContainer key={tag}>
              <Link to={FormatTagHandle(tag)} aria-label={CreateAriaLabel(tag)}>
                {tag}
              </Link>
            </BlogTagContainer>
          ))}
        </TagsContainer>
      </BlogPostBottom>
    </BlogPost>
  </PageWrapper>
);

BlogArticle.propTypes = {
  article: PropTypes.shape({
    authorV2: PropTypes.shape({
      name: PropTypes.string,
    }),
    title: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string,
      altText: PropTypes.string,
    }),
    author: PropTypes.shape({
      name: PropTypes.string,
    }),
    publishedAt: PropTypes.string,
    contentHtml: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default BlogArticle;
